import React from "react";
import { cash } from "../../../services/api";
import Picture from "../../utils/Picture";

/**
 * Step 2 — choose a kind of room. The guest picks the category; the exact
 * room or bed is assigned by Makua and kept for the whole stay (spec 03).
 * Prices are what this party pays on top of the included room, for the
 * whole stay — not per night, which nobody can add up in their head.
 */
const StepRoom = ({ d, set, options }) => {
  const groups = [];
  options.categories.forEach((c) => {
    let g = groups.find((x) => x.name === c.group);
    if (!g) groups.push((g = { name: c.group, items: [] }));
    g.items.push(c);
  });
  const base = options.categories.find((c) => c.isBase);

  return (
    <section className="bk-step" aria-labelledby="s2">
      <h2 id="s2" className="bk-step-title">Where you'll stay</h2>
      <p className="mk-muted">
        {base ? `A bed in ${base.name} is included in the retreat price. Anything else shows what it adds for your whole stay.` : "Choose the kind of room you'd like."}{" "}
        We assign the room itself and keep it for you every night.
      </p>

      {groups.map((g) => (
        <fieldset key={g.name} className="bk-group">
          <legend className="bk-group-name hFont">{g.name}</legend>
          <div className="bk-rooms">
            {g.items.map((c) => <RoomCard key={c.id} c={c} picked={d.room_type_id === c.id} guests={d.guests} onPick={() => set({ room_type_id: c.id })} />)}
          </div>
        </fieldset>
      ))}
    </section>
  );
};

const RoomCard = ({ c, picked, guests, onPick }) => {
  const unavailable = !c.fits;
  const price = c.isBase ? "Included"
    : c.requestOnly ? "Price on request"
    : c.upgradeForStay > 0 ? `+${cash(c.upgradeForStay)}` : "No extra charge";
  const sleeps = c.sellBy === "bed" ? "A bed in a shared room" : `Sleeps up to ${c.maxGuests}`;
  const units = !c.requestOnly && c.sellBy === "room" && c.unitsNeeded > 1 ? `${c.unitsNeeded} rooms for ${guests} guests` : null;
  // "N left" only once some have gone: a one-of-a-kind cabana isn't scarce, it's unique.
  const left = !c.requestOnly && c.fits && c.available != null && c.available <= 3 && c.available < c.unitCount
    ? `${c.available} ${c.sellBy === "bed" ? (c.available === 1 ? "bed" : "beds") : (c.available === 1 ? "room" : "rooms")} left` : null;

  return (
    <label className={`bk-room ${picked ? "is-picked" : ""} ${unavailable ? "is-off" : ""}`}>
      <input type="radio" name="room" className="visually-hidden" checked={picked} disabled={unavailable} onChange={onPick} />
      <span className="bk-room-img">
        {c.photos[0] ? <Picture src={c.photos[0]} sizes="(min-width: 992px) 260px, 100vw" width={320} height={200} /> : <span className="bk-room-ph hFont" aria-hidden="true">{c.name}</span>}
        {c.isBase && <span className="bk-badge">Included</span>}
        {c.requestOnly && <span className="bk-badge bk-badge--clay">By request</span>}
      </span>
      <span className="bk-room-body">
        <span className="bk-room-name">{c.name}</span>
        {c.summary && <span className="bk-room-sum">{c.summary}</span>}
        <span className="bk-room-facts">
          <span>{sleeps}</span>
          {c.bathroom && <span>{c.bathroom} bathroom</span>}
          <span>{c.mealService === "plated" ? "Plated meals" : "Buffet meals"}</span>
          {c.amenities.slice(0, 3).map((a) => <span key={a}>{a}</span>)}
        </span>
        <span className="bk-room-foot">
          <span className="bk-room-price">{price}{!c.isBase && !c.requestOnly && c.upgradeForStay > 0 && <small> for your stay</small>}</span>
          <span className="bk-room-state">
            {unavailable ? "Not available for your dates" : picked ? "Selected" : left || units || "Select"}
          </span>
        </span>
      </span>
    </label>
  );
};

export default StepRoom;
